import { APP_V2_APP_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
import { Platform } from "@src/mobile/utils/platform";

export interface LiveUpdateReadyResult {
  currentBundleId: string | null;
  previousBundleId: string | null;
  rollback: boolean;
}

/**
 * Signals to the Live Update plugin that the app has loaded successfully.
 * This prevents automatic rollback when readyTimeout is configured.
 * Logs the result to Datadog. On web, this is a no-op.
 */
export async function signalLiveUpdateReady(): Promise<LiveUpdateReadyResult> {
  if (!Platform.isMobile) {
    return { currentBundleId: null, previousBundleId: null, rollback: false };
  }

  const { LiveUpdate } = await import("@capawesome/capacitor-live-update");
  const result = await LiveUpdate.ready();

  if (result.rollback) {
    logEvent(APP_V2_APP_EVENTS.LIVE_UPDATE_ROLLBACK, {
      currentBundleId: result.currentBundleId,
      previousBundleId: result.previousBundleId,
    });
  } else {
    logEvent(APP_V2_APP_EVENTS.LIVE_UPDATE_READY, {
      currentBundleId: result.currentBundleId,
    });
  }

  return result;
}

/**
 * Returns the marketing version of the installed native binary (e.g. "3.7.2").
 * Returns null on web.
 */
export async function getNativeVersionName(): Promise<string | null> {
  if (!Platform.isMobile) {
    return null;
  }

  const { LiveUpdate } = await import("@capawesome/capacitor-live-update");
  const { versionName } = await LiveUpdate.getVersionName();
  return versionName;
}

/**
 * Returns the currently active live update bundle ID.
 * Returns null if running the default built-in bundle (no OTA applied), on web, or on error.
 */
export async function getCurrentBundleId(): Promise<string | null> {
  if (!Platform.isMobile) {
    return null;
  }

  try {
    const { LiveUpdate } = await import("@capawesome/capacitor-live-update");
    const result = await LiveUpdate.getCurrentBundle();
    return result.bundleId ?? null;
  } catch (error: unknown) {
    logError(APP_V2_APP_EVENTS.LIVE_UPDATE_READY_FAILURE, { error });
    return null;
  }
}
